import React, { useState } from 'react';
import axios from 'axios';
import {
  Search, Hash, Package, Factory, ShoppingCart, ShieldCheck, Loader2, AlertTriangle,
  ArrowRight, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const money = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const inputCls = "w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-[#C6A15B]/60 focus:outline-none";

const Section = ({ icon: Icon, title, children, testid }) => (
  <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5" data-testid={testid}>
    <h3 className="text-white font-semibold flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#C6A15B]" /> {title}</h3>
    {children}
  </div>
);
const Row = ({ label, value, mono, accent }) => (
  <div className="flex justify-between gap-3 py-1 text-sm"><span className="text-slate-500">{label}</span><span className={`text-right ${mono ? 'font-mono' : ''} ${accent ? 'text-[#C6A15B]' : 'text-slate-200'}`}>{value ?? '—'}</span></div>
);

export const SerialTraceability = ({ user, setActiveTab }) => {
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (e) => {
    e?.preventDefault();
    if (q.trim().length < 3) { toast.error('Enter at least 3 characters'); return; }
    setLoading(true); setResult(null);
    try { const r = await axios.get(`${API}/api/serial-numbers/trace?serial=${encodeURIComponent(q.trim())}`); setResult(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Search failed'); }
    finally { setLoading(false); }
  };

  const steps = [];
  if (result?.found) {
    if (result.product) steps.push('Produced');
    if (result.records?.length) steps.push('Serial Assigned');
    if (result.order) steps.push(`Order ${result.order.order_number || ''}`.trim());
    if (result.order?.status) steps.push(result.order.status.replace(/_/g, ' '));
    if (result.certificate) steps.push('Certificate Issued');
  }

  return (
    <div className="space-y-6" data-testid="serial-traceability">
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white">Serial Search &amp; Traceability</h2>
        <p className="text-slate-400 text-sm mt-1">Enter a serial number to trace it across product, order and certificate records.</p>
      </div>

      <form onSubmit={run} className="flex gap-3 max-w-2xl">
        <div className="relative flex-1">
          <Hash className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value.toUpperCase())} placeholder="Enter serial number..." className={`${inputCls} pl-9 font-mono`} data-testid="serial-search-input" />
        </div>
        <button type="submit" disabled={loading} className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg bg-[#C6A15B] text-black font-medium disabled:opacity-60 shrink-0" data-testid="serial-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Trace
        </button>
      </form>

      {result && !result.found && (
        <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl py-14 text-center" data-testid="serial-not-found">
          <AlertTriangle className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">No record found</p>
          <p className="text-slate-500 text-sm mt-1">We couldn't find anything matching "{result.serial}". Check the value and try again.</p>
        </div>
      )}

      {result?.found && (
        <div className="space-y-5">
          {/* Timeline */}
          {steps.length > 0 && (
            <div className="bg-[#14171B] border border-white/[0.08] rounded-2xl p-5" data-testid="serial-timeline">
              <div className="flex items-center gap-2 flex-wrap text-sm">
                {steps.map((s, i) => (
                  <React.Fragment key={i}>
                    <span className="inline-flex items-center gap-1.5 text-slate-200"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> {s}</span>
                    {i < steps.length - 1 && <ArrowRight className="w-4 h-4 text-slate-600" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Section icon={Hash} title="Identifier" testid="trace-identifier">
              <Row label="Serial" value={result.serial} mono />
              <Row label="Records" value={`${result.records?.length || 0} assignment(s)`} />
              {result.records?.[0]?.status && <Row label="Status" value={result.records[0].status} />}
            </Section>

            {result.product ? (
              <Section icon={Package} title="Product" testid="trace-product">
                <Row label="Product" value={result.product.name} />
                {result.product.sku && <Row label="SKU" value={result.product.sku} mono />}
                <Row label="Category" value={result.product.category} />
              </Section>
            ) : <Section icon={Package} title="Product" testid="trace-product"><p className="text-slate-500 text-sm">No linked product.</p></Section>}

            {result.order ? (
              <Section icon={ShoppingCart} title="Order" testid="trace-order">
                <Row label="Order #" value={result.order.order_number} mono accent />
                <Row label="Status" value={(result.order.status || '').replace(/_/g, ' ')} />
                <Row label="Date" value={fmtDate(result.order.created_at)} />
                <Row label="Customer" value={result.order.customer_company || result.order.customer_name} />
                <Row label="Total" value={money(result.order.total_amount)} />
                {setActiveTab && <button onClick={() => setActiveTab('orders')} className="mt-2 text-[#C6A15B] text-sm inline-flex items-center gap-1 hover:underline" data-testid="trace-open-order">Open in Orders <ArrowRight className="w-3.5 h-3.5" /></button>}
              </Section>
            ) : <Section icon={ShoppingCart} title="Order" testid="trace-order"><p className="text-slate-500 text-sm">Not linked to an order.</p></Section>}

            {result.certificate ? (
              <Section icon={ShieldCheck} title="Installation Certificate" testid="trace-certificate">
                <Row label="Serial" value={result.certificate.serial_number} mono />
                <Row label="Status" value={result.certificate.status} />
                <Row label="Roll" value={result.certificate.roll_number} mono accent />
                <Row label="Coverage" value={result.certificate.coverage_summary} />
                <Row label="Installed" value={fmtDate(result.certificate.installation_date)} />
                {setActiveTab && <button onClick={() => setActiveTab('warranty')} className="mt-2 text-[#C6A15B] text-sm inline-flex items-center gap-1 hover:underline" data-testid="trace-open-cert">Open in Warranty <ArrowRight className="w-3.5 h-3.5" /></button>}
              </Section>
            ) : <Section icon={ShieldCheck} title="Installation Certificate" testid="trace-certificate"><p className="text-slate-500 text-sm">No certificate issued for this serial.</p></Section>}
          </div>
        </div>
      )}
    </div>
  );
};

export default SerialTraceability;
